import { c as _c } from "react/compiler-runtime";
import { Heading } from "@/components/ui/Heading";
import { ScrollArea } from "@/components/ui/ScrollArea";
import { Text } from "@/components/ui/Text";
import * as React from "react";
import cn from "twsx";

// Building blocks for pages that need more than the ListPage, StandardPage,
// ProfilePage and FormPage templates: header groups and rows, hero titles,
// body content columns, sections, form columns, an aside panel and a split
// view. Every page chrome part lives in components/shell.

type PartProps = {
  children: React.ReactNode;
  className?: string;
};

// ── Header ────────────────────────────────────────────────────────────────────

export function PageHeading(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <Heading level={1} size="sm" weight="medium" truncate={true} className="flex items-center self-stretch">{children}</Heading>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function PageHeaderMain(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex min-w-0 flex-1 items-center gap-2", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
export function PageHeaderGroup(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex shrink-0 items-center gap-2", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
export function PageHeaderStack(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("-ml-4 -mr-2.5 flex min-w-0 flex-1 flex-col", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

// A 44px row inside the page chrome. `list` insets it to the list column.
export function PageRow(t0) {
  const $ = _c(7);
  const {
    children,
    className,
    inset: t1,
    divider: t2
  } = t0;
  const inset = t1 === undefined ? "shell" : t1;
  const divider = t2 === undefined ? false : t2;
  const t3 = inset === "list" ? "px-5 md:px-page-inset" : "pl-4 pr-2.5";
  const t4 = divider && "border-b border-border-secondary";
  let t5;
  if ($[0] !== className || $[1] !== t3 || $[2] !== t4) {
    t5 = cn("flex min-h-11 w-full min-w-0 items-center justify-between gap-3", t3, t4, className);
    $[0] = className;
    $[1] = t3;
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== children || $[5] !== t5) {
    t6 = <div className={t5}>{children}</div>;
    $[4] = children;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  return t6;
}

// A page-level Alert above the header, inset like list rows.
/** Use when a page needs a full-width notice above its header. */
export function PageBanner(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="w-full min-w-0 px-5 pt-3 md:px-page-inset">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function PageHeroRow(t0) {
  const $ = _c(6);
  const {
    children,
    className,
    divider: t1
  } = t0;
  const divider = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== className) {
    t2 = cn("min-h-row-hero items-end pb-2 pt-4 max-md:min-h-0 max-md:flex-wrap max-md:items-center max-md:py-2", "only:min-h-20 only:items-center only:border-b only:border-border-secondary only:py-0", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children || $[3] !== divider || $[4] !== t2) {
    t3 = <PageRow inset="list" divider={divider} className={t2}>{children}</PageRow>;
    $[2] = children;
    $[3] = divider;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
export function ProfileHeroRow(t0) {
  const $ = _c(6);
  const {
    children,
    className,
    divider: t1
  } = t0;
  const divider = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== className) {
    t2 = cn("min-h-row-profile-hero items-center py-4 max-md:min-h-0 max-md:items-start max-md:pb-2 max-md:pt-3", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children || $[3] !== divider || $[4] !== t2) {
    t3 = <PageHeroRow divider={divider} className={t2}>{children}</PageHeroRow>;
    $[2] = children;
    $[3] = divider;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
export function PageTabsRow(t0) {
  const $ = _c(6);
  const {
    children,
    className,
    divider: t1
  } = t0;
  const divider = t1 === undefined ? true : t1;
  const t2 = divider && "border-b border-border-secondary";
  let t3;
  if ($[0] !== className || $[1] !== t2) {
    t3 = cn("relative items-end pb-1", t2, className);
    $[0] = className;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== children || $[4] !== t3) {
    t4 = <PageRow inset="list" className={t3}>{children}</PageRow>;
    $[3] = children;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
export function PageToolsRow(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("min-h-16 border-border-primary max-md:min-h-0 max-md:items-center max-md:gap-1.5 max-md:py-2", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <PageRow inset="list" divider={true} className={t1}>{children}</PageRow>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
export function PageHeroTitle(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex min-w-0 max-w-full items-baseline", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <Heading level={1} size="xl" weight="medium" className={t1}>{children}</Heading>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
export function PageHeroSubtitle(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("min-w-0 text-sm leading-5 text-text-secondary", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

// Entity name with its record number (for example "(#88086)") as a muted suffix.
export function ProfileHeroTitle(t0) {
  const $ = _c(10);
  const {
    children,
    number,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("w-full flex-wrap gap-x-2 gap-y-0 overflow-visible", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <span className="min-w-0 whitespace-normal break-words">{children}</span>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== number) {
    t3 = number !== null && number !== undefined && number !== "" && <Text size="inherit" tone="tertiary" className="shrink-0 whitespace-nowrap">(#{number})</Text>;
    $[4] = number;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <PageHeroTitle className={t1}>{t2}{t3}</PageHeroTitle>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}

// ── Body ──────────────────────────────────────────────────────────────────────

export function PageMain(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex min-h-0 w-full flex-1 flex-col overflow-hidden", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

// The body's content column. `wide` caps it at 1280px; `form` and `formWide`
// use the form widths.
export function PageContent(t0) {
  const $ = _c(8);
  const {
    children,
    className,
    width: t1
  } = t0;
  const width = t1 === undefined ? "full" : t1;
  const t2 = width === "wide" && "mx-auto max-w-7xl";
  const t3 = width === "form" && "mx-auto max-w-form";
  const t4 = width === "formWide" && "mx-auto max-w-form-wide";
  let t5;
  if ($[0] !== className || $[1] !== t2 || $[2] !== t3 || $[3] !== t4) {
    t5 = cn("group/dashboard-body-content flex w-full flex-col gap-6 py-5 pl-gutter-l pr-gutter-r md:p-4", t2, t3, t4, className);
    $[0] = className;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== children || $[6] !== t5) {
    t6 = <div data-content-gutter="true" className={t5}>{children}</div>;
    $[5] = children;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  return t6;
}

// One titled group in an overview body. The heading hugs its content (16px);
// PageContent spaces whole sections (24px).
export function PageSection(t0) {
  const $ = _c(10);
  const {
    title,
    description,
    actions,
    className,
    children
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex w-full min-w-0 flex-col gap-4", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== actions || $[3] !== description || $[4] !== title) {
    t2 = (title || actions) && <div className="flex min-w-0 flex-col items-stretch gap-3 md:flex-row md:items-start md:justify-between"><div className="flex min-w-0 flex-1 flex-col gap-0.5">{title && <Heading level={2}>{title}</Heading>}{description && <Text as="p" tone="tertiary">{description}</Text>}</div>{actions && <div className="flex w-full flex-wrap items-center gap-2 md:w-auto md:shrink-0">{actions}</div>}</div>;
    $[2] = actions;
    $[3] = description;
    $[4] = title;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== children || $[7] !== t1 || $[8] !== t2) {
    t3 = <section className={t1}>{t2}{children}</section>;
    $[6] = children;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
export function PageSectionHeader(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex w-full min-w-0 flex-col items-stretch gap-3 py-3 md:flex-row md:items-center md:justify-between md:gap-2 md:py-4", "group-data-[padded=false]/dashboard-screen-body:pl-gutter-l group-data-[padded=false]/dashboard-screen-body:pr-gutter-r", "md:group-data-[padded=false]/dashboard-screen-body:px-8", "group-data-[content-gutter=true]/dashboard-body-content:px-0", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div data-dashboard-body-header={true} className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
export function PageSectionHeaderGroup(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex items-center gap-x-1", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
export function PageSectionTitle(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="text-xl font-semibold leading-7 md:text-3xl md:font-medium md:leading-normal">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

// ── Form column ───────────────────────────────────────────────────────────────
// `page` is a centred form column; `flush` fills a sheet edge to edge and lets
// the form own its padding.

export function PageForm(t0) {
  const $ = _c(10);
  const {
    children,
    className,
    variant: t1,
    width: t2
  } = t0;
  const variant = t1 === undefined ? "page" : t1;
  const width = t2 === undefined ? "default" : t2;
  const t3 = variant !== "flush" && width === "default" && "max-w-form";
  const t4 = variant !== "flush" && width === "wide" && "max-w-form-wide";
  const t5 = variant === "page" && "min-h-full px-6 py-10 md:px-form-page-x md:py-form-page-y";
  const t6 = variant === "flush" && "relative h-full overflow-hidden";
  let t7;
  if ($[0] !== className || $[1] !== t3 || $[2] !== t4 || $[3] !== t5 || $[4] !== t6) {
    t7 = cn("mx-auto flex w-full min-w-0 flex-col", t3, t4, t5, t6, className);
    $[0] = className;
    $[1] = t3;
    $[2] = t4;
    $[3] = t5;
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== children || $[7] !== t7 || $[8] !== variant) {
    t8 = <div data-dashboard-form-layout={variant} className={t7}>{children}</div>;
    $[6] = children;
    $[7] = t7;
    $[8] = variant;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  return t8;
}
export function PageFormHeader(t0) {
  const $ = _c(10);
  const {
    children,
    actions,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex w-full min-w-0 items-start justify-between gap-8", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <div className="flex min-w-0 flex-1 flex-col gap-3">{children}</div>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== actions) {
    t3 = actions && <div className="flex shrink-0 items-center justify-end pt-1">{actions}</div>;
    $[4] = actions;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <div className={t1}>{t2}{t3}</div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
export function PageFormTitle(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("max-w-measure-title", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <PageHeroTitle className={t1}>{children}</PageHeroTitle>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
export function PageFormDescription(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("max-w-measure", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <Text as="p" size="lg" tone="secondary" className={t1}>{children}</Text>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
export function PageFormFooter(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex shrink-0 justify-end gap-2", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

// ── Aside and split view ──────────────────────────────────────────────────────

// `fill` renders the aside's content full width inside a dialog or sheet.
export function PageAside(t0) {
  const $ = _c(6);
  const {
    children,
    className,
    layout: t1
  } = t0;
  const layout = t1 === undefined ? "aside" : t1;
  const t2 = layout === "aside" ? "w-96 min-w-96 max-w-96 border-l border-border-primary" : "min-h-0 w-full flex-1";
  let t3;
  if ($[0] !== className || $[1] !== t2) {
    t3 = cn("flex flex-col overflow-hidden bg-bg-surface", t2, className);
    $[0] = className;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== children || $[4] !== t3) {
    t4 = <div className={t3}>{children}</div>;
    $[3] = children;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
export function PageAsideHeader(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex h-14 items-center justify-between px-6", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
export function PageAsideTitle(t0) {
  const $ = _c(3);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== className) {
    t1 = <Text as="div" tone="tertiary" className={className}>{children}</Text>;
    $[0] = children;
    $[1] = className;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
export function PageAsideBody(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex w-full flex-col items-start justify-start gap-8 p-6 pb-12", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <ScrollArea className="min-h-0 flex-1" viewportClassName="overflow-x-hidden"><div className={t1}>{children}</div></ScrollArea>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

// Split view (inbox): a full-width list on mobile, a 440px list beside the
// detail pane from md up. The detail pane is hidden on mobile.
// `stackBelowLg` stacks the panes vertically below lg (application form beside its reference panel).
// `surface` paints the split on the surface colour (inbox).
export function PageSplit(t0) {
  const $ = _c(6);
  const {
    children,
    stackBelowLg: t1,
    surface: t2
  } = t0;
  const stackBelowLg = t1 === undefined ? false : t1;
  const surface = t2 === undefined ? false : t2;
  const t3 = surface && "bg-bg-surface";
  const t4 = stackBelowLg && "flex-col lg:flex-row";
  let t5;
  if ($[0] !== t3 || $[1] !== t4) {
    t5 = cn("flex min-h-0 flex-1 overflow-hidden", t3, t4);
    $[0] = t3;
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== children || $[4] !== t5) {
    t6 = <div className={t5}>{children}</div>;
    $[3] = children;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  return t6;
}
export function PageSplitSidebar(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="flex h-full w-full min-w-0 shrink-0 flex-col border-r border-border-subtle md:w-split-list">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function PageSplitHeader(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="flex min-h-11 shrink-0 items-center justify-between gap-3 border-b border-border-subtle pl-4 pr-2.5">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function PageSplitToolbar(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="flex min-h-10 shrink-0 flex-wrap items-center gap-2 border-b border-border-subtle px-3 py-2">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function PageSplitSidebarBody(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2 pb-3 pt-2">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

// `collapse` hides the pane below md, where the list takes the full width.
export function PageSplitBody(t0) {
  const $ = _c(5);
  const {
    children,
    collapse: t1
  } = t0;
  const collapse = t1 === undefined ? true : t1;
  const t2 = collapse ? "hidden md:flex" : "flex";
  let t3;
  if ($[0] !== t2) {
    t3 = cn("min-w-0 flex-1 flex-col overflow-hidden", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== children || $[3] !== t3) {
    t4 = <div className={t3}>{children}</div>;
    $[2] = children;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}
export function PageSplitDetail(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="min-h-0 flex-1 overflow-y-auto p-8"><div className="flex max-w-detail flex-col gap-5">{children}</div></div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}